import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";

import axiosInstance from "../../../../../services/AxiosInstance";
import {
  notifyError,
  notifySuccess,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import DatePickerSingle from "jsx/components/CitoPlus/components/DatePickerSingle";
import CheckboxOnOff from "jsx/components/CitoPlus/components/CheckboxOnOff";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";
import ZonaSocialSelect from "jsx/components/CitoPlus/components/ZonaSocial/ZonaSocialSelect";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";

const BLOQUEOS_URL = "/api/v3/zonas-sociales/bloqueos/";
const BLOQUEOS_CALENDARIO_URL = "/api/v3/zonas-sociales/bloqueos/calendario/";
const ZONAS_URL = "/api/v3/zonas-sociales/zonas/dropdown/";

const MOTIVO_OPTIONS = Object.freeze([
  { value: "mantenimiento", label: "Mantenimiento" },
  { value: "evento", label: "Evento especial" },
  { value: "otro", label: "Otro" },
]);

const DEFAULT_FORM_STATE = Object.freeze({
  titulo: "",
  descripcion: "",
  motivo: "mantenimiento",
  zona_uuid: "",
  zona_nombre: "",
  start: null,
  end: null,
  all_day: false,
  activo: true,
});

const ensureDate = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseBlockDetail = (detail) => {
  if (!detail) {
    return { ...DEFAULT_FORM_STATE };
  }

  return {
    titulo: detail.titulo || "",
    descripcion: detail.descripcion || "",
    motivo: detail.motivo || "mantenimiento",
    zona_uuid: detail.zona?.uuid || detail.zona_uuid || "",
    zona_nombre:
      detail.zona?.nombre || detail.zona_nombre || detail.zona?.label || "",
    start: ensureDate(detail.fecha_inicio || detail.start),
    end: ensureDate(detail.fecha_fin || detail.end),
    all_day: Boolean(detail.all_day),
    activo: detail.activo !== undefined ? Boolean(detail.activo) : true,
  };
};

const normalizeZones = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload?.results && Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
};

const BloqueoGestionModal = ({
  show,
  bloqueoUuid = null,
  onClose,
  onSaved,
  cachedZones = [],
  initialData = null,
}) => {
  const isEditing = Boolean(bloqueoUuid);

  const [zones, setZones] = useState(
    Array.isArray(cachedZones) ? cachedZones : [],
  );
  const [detail, setDetail] = useState(null);
  const [formState, setFormState] = useState({ ...DEFAULT_FORM_STATE });

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingZones, setIsLoadingZones] = useState(false);

  const motivoOptions = useMemo(() => MOTIVO_OPTIONS, []);

  const zoneOptions = useMemo(
    () =>
      zones.map((zone) => ({
        uuid: zone.uuid || zone.value || zone.id,
        nombre: zone.nombre || zone.label || zone.name,
      })),
    [zones],
  );

  const resetState = useCallback(() => {
    setDetail(null);
    setFormState({ ...DEFAULT_FORM_STATE });
    setIsLoadingDetail(false);
    setIsSaving(false);
  }, []);

  const hydrateZones = useCallback(
    async (signal) => {
      if (Array.isArray(cachedZones) && cachedZones.length > 0) {
        setZones(cachedZones);
        return;
      }

      setIsLoadingZones(true);
      try {
        const response = await axiosInstance.get(ZONAS_URL, {
          signal,
        });
        setZones(normalizeZones(response?.data));
      } catch (error) {
        if (!signal?.aborted) {
          notifyError(
            "No fue posible obtener el listado de zonas sociales disponibles.",
          );
        }
      } finally {
        if (!signal?.aborted) {
          setIsLoadingZones(false);
        }
      }
    },
    [cachedZones],
  );

  const hydrateDetail = useCallback(async (uuid, signal) => {
    if (!uuid) {
      setDetail(null);
      setFormState({ ...DEFAULT_FORM_STATE });
      return;
    }

    setIsLoadingDetail(true);
    try {
      const response = await axiosInstance.get(`${BLOQUEOS_URL}${uuid}/`, {
        signal,
      });
      if (signal?.aborted) {
        return;
      }
      const fetchedDetail = response.data;
      setDetail(fetchedDetail);
      setFormState(parseBlockDetail(fetchedDetail));
    } catch (error) {
      if (!signal?.aborted) {
        notifyError("No fue posible cargar la información del bloqueo.");
        setDetail(null);
        setFormState({ ...DEFAULT_FORM_STATE });
      }
    } finally {
      if (!signal?.aborted) {
        setIsLoadingDetail(false);
      }
    }
  }, []);

  const applyInitialDetail = useCallback((data) => {
    if (!data) {
      return false;
    }
    setDetail(data);
    setFormState(parseBlockDetail(data));
    setIsLoadingDetail(false);
    return true;
  }, []);

  useEffect(() => {
    if (!show) {
      resetState();
      return undefined;
    }

    const controller = new AbortController();

    hydrateZones(controller.signal);

    if (!bloqueoUuid && initialData && applyInitialDetail(initialData)) {
      return () => controller.abort();
    }

    hydrateDetail(bloqueoUuid, controller.signal);

    return () => controller.abort();
  }, [
    applyInitialDetail,
    bloqueoUuid,
    hydrateDetail,
    hydrateZones,
    initialData,
    resetState,
    show,
  ]);

  const handleDismiss = useCallback(() => {
    resetState();
    onClose?.();
  }, [onClose, resetState]);

  const updateFormField = useCallback((name, value) => {
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      updateFormField(name, value);
    },
    [updateFormField],
  );

  const handleCheckboxChange = useCallback(
    (event) => {
      const { name, checked } = event.target;
      updateFormField(name, checked);
    },
    [updateFormField],
  );

  const handleDateRangeChange = useCallback(
    (values) => {
      const [start, end] = Array.isArray(values) ? values : [null, null];
      updateFormField("start", start ? new Date(start) : null);
      updateFormField("end", end ? new Date(end) : null);
    },
    [updateFormField],
  );

  const handleZonaChange = useCallback(
    (newValue, option) => {
      updateFormField("zona_uuid", newValue || "");
      updateFormField(
        "zona_nombre",
        option?.nombre || option?.label || option?.name || "",
      );
    },
    [updateFormField],
  );

  const validateForm = useCallback(() => {
    if (!formState.titulo.trim()) {
      notifyError("Debes ingresar un título para el bloqueo.");
      return false;
    }

    if (!formState.zona_uuid) {
      notifyError("Debes seleccionar la zona social que se bloqueará.");
      return false;
    }

    const startDate = ensureDate(formState.start);
    const endDate = ensureDate(formState.end);

    if (!startDate || !endDate) {
      notifyError("Debes seleccionar el rango de fechas para el bloqueo.");
      return false;
    }

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      notifyError("Las fechas seleccionadas no son válidas.");
      return false;
    }

    if (startDate.getTime() >= endDate.getTime()) {
      notifyError(
        "La fecha y hora final del bloqueo debe ser posterior al inicio.",
      );
      return false;
    }

    return true;
  }, [formState.end, formState.start, formState.titulo, formState.zona_uuid]);

  const buildPayload = useCallback(() => {
    const payload = {
      titulo: formState.titulo.trim(),
      descripcion: formState.descripcion?.trim() || "",
      motivo: formState.motivo || "mantenimiento",
      zona_uuid: formState.zona_uuid,
      fecha_inicio: ensureDate(formState.start)?.toISOString(),
      fecha_fin: ensureDate(formState.end)?.toISOString(),
      all_day: Boolean(formState.all_day),
      activo: Boolean(formState.activo),
    };

    return payload;
  }, [formState]);

  const extractErrorMessage = (error) => {
    if (!error) {
      return null;
    }

    const responseData = error.response?.data;
    if (!responseData) {
      return null;
    }

    if (typeof responseData === "string") {
      return responseData;
    }

    if (Array.isArray(responseData)) {
      return responseData.join(" ");
    }

    if (typeof responseData === "object") {
      const messages = Object.entries(responseData).flatMap(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map((item) => `${key}: ${item}`);
        }
        if (typeof value === "string") {
          return `${key}: ${value}`;
        }
        return [];
      });
      return messages.join(" ");
    }

    return null;
  };

  const handleSave = useCallback(async () => {
    if (isSaving) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    const payload = buildPayload();

    try {
      const request = isEditing
        ? axiosInstance.patch(`${BLOQUEOS_URL}${bloqueoUuid}/`, payload)
        : axiosInstance.post(BLOQUEOS_URL, payload);

      const response = await request;
      const saved = response?.data || payload;

      notifySuccess(
        isEditing
          ? "Bloqueo actualizado correctamente."
          : "Bloqueo creado correctamente.",
      );
      onSaved?.(saved);
      handleDismiss();
    } catch (error) {
      handleErrors(error);
    } finally {
      setIsSaving(false);
    }
  }, [
    bloqueoUuid,
    buildPayload,
    handleDismiss,
    isEditing,
    isSaving,
    onSaved,
    validateForm,
  ]);

  const handleFetchConflicts = useCallback(async () => {
    if (!formState.zona_uuid || !formState.start || !formState.end) {
      notifyError(
        "Selecciona la zona social y el rango de fechas antes de consultar bloqueos.",
      );
      return;
    }

    try {
      const response = await axiosInstance.get(BLOQUEOS_CALENDARIO_URL, {
        params: {
          zona: formState.zona_uuid,
          start: ensureDate(formState.start)?.toISOString(),
          end: ensureDate(formState.end)?.toISOString(),
        },
      });

      const conflicts = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];

      if (conflicts.length === 0) {
        notifySuccess("No hay otros bloqueos en el rango seleccionado.");
        return;
      }

      notifyError(
        `Existen ${conflicts.length} bloqueo(s) que se superponen con el rango seleccionado.`,
      );
    } catch (error) {
      const message =
        extractErrorMessage(error) ||
        "No fue posible verificar los bloqueos existentes.";
      notifyError(message);
    }
  }, [formState.end, formState.start, formState.zona_uuid]);

  const conflictsDisabled =
    !formState.zona_uuid || !formState.start || !formState.end;

  return (
    <Modal
      show={show}
      onHide={handleDismiss}
      size="lg"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="fas fa-ban" aria-hidden="true" />
          {isEditing ? "Editar bloqueo" : "Crear bloqueo"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {(isLoadingDetail || isLoadingZones) && (
          <div className="py-4 text-center">
            <SpinnerCitoPlus text="Cargando información del bloqueo..." />
          </div>
        )}

        {!(isLoadingDetail || isLoadingZones) && (
          <form className="row gy-3">
            <div className="col-12">
              <label
                htmlFor="bloqueo-titulo"
                className="form-label fw-semibold"
              >
                Título del bloqueo
              </label>
              <input
                id="bloqueo-titulo"
                name="titulo"
                type="text"
                className="form-control"
                value={formState.titulo}
                onChange={handleInputChange}
                maxLength={150}
                placeholder="Ej. Mantenimiento piscina"
                required
              />
            </div>

            <div className="col-md-6">
              <label
                htmlFor="bloqueo-motivo"
                className="form-label fw-semibold"
              >
                Motivo
              </label>
              <select
                id="bloqueo-motivo"
                name="motivo"
                className="form-select"
                value={formState.motivo}
                onChange={handleInputChange}
              >
                {motivoOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12">
              <div className="row g-2 align-items-end">
                <div className="col-lg-8 col-md-7">
                  <ZonaSocialSelect
                    name="zona_uuid"
                    value={formState.zona_uuid}
                    onChange={handleZonaChange}
                    options={zoneOptions}
                    autoFetch={false}
                    allowEmptyOption
                    placeholder="Selecciona una zona"
                    loading={isLoadingZones}
                    selectedLabel={formState.zona_nombre}
                    label="Zona social"
                    showLabel
                  />
                </div>
                <div className="col-lg-4 col-md-5">
                  <OverlayTrigger
                    placement="left"
                    overlay={
                      <Tooltip id="bloqueo-conflicts-tooltip">
                        Buscar bloqueos existentes en el rango seleccionado
                      </Tooltip>
                    }
                  >
                    <span className="d-inline-block w-100" tabIndex={0}>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm w-100"
                        onClick={handleFetchConflicts}
                        disabled={conflictsDisabled}
                        aria-label="Buscar bloqueos existentes en el rango seleccionado"
                        style={
                          conflictsDisabled
                            ? { pointerEvents: "none" }
                            : undefined
                        }
                      >
                        <i className="fas fa-search me-2" aria-hidden="true" />
                        Verificar bloqueos existentes
                      </button>
                    </span>
                  </OverlayTrigger>
                </div>
              </div>
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold mb-2">
                Rango de fechas y horas
              </label>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small text-muted" htmlFor="bloqueo-inicio">
                    Fecha y hora de inicio
                  </label>
                  <DatePickerSingle
                    id="bloqueo-inicio"
                    selectedDate={formState.start}
                    onChange={(date) => updateFormField("start", date)}
                    showTimeSelect
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm"
                    withPortal
                    placeholderText="Selecciona inicio"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small text-muted" htmlFor="bloqueo-fin">
                    Fecha y hora de fin
                  </label>
                  <DatePickerSingle
                    id="bloqueo-fin"
                    selectedDate={formState.end}
                    onChange={(date) => updateFormField("end", date)}
                    showTimeSelect
                    timeIntervals={15}
                    dateFormat="dd/MM/yyyy HH:mm"
                    minDate={formState.start}
                    withPortal
                    placeholderText="Selecciona fin"
                  />
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <CheckboxOnOff
                id="bloqueo-all-day"
                name="all_day"
                label="Aplicar bloqueo para todo el día"
                isChecked={Boolean(formState.all_day)}
                onChange={handleCheckboxChange}
              />
            </div>

            <div className="col-md-6">
              <CheckboxOnOff
                id="bloqueo-activo"
                name="activo"
                label="Bloqueo activo"
                isChecked={Boolean(formState.activo)}
                onChange={handleCheckboxChange}
              />
            </div>

            <div className="col-12">
              <label
                htmlFor="bloqueo-descripcion"
                className="form-label fw-semibold"
              >
                Descripción
              </label>
              <textarea
                id="bloqueo-descripcion"
                name="descripcion"
                className="form-control"
                rows={3}
                value={formState.descripcion}
                onChange={handleInputChange}
                placeholder="Describe el motivo del bloqueo o instrucciones adicionales."
              />
            </div>
          </form>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleDismiss}
          disabled={isSaving}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving || isLoadingDetail || isLoadingZones}
        >
          {isSaving ? (
            <>
              <i
                className="fas fa-circle-notch fa-spin me-2"
                aria-hidden="true"
              />
              Guardando...
            </>
          ) : (
            <>
              <i className="fas fa-save me-2" aria-hidden="true" />
              Guardar bloqueo
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BloqueoGestionModal;
